import { ref, type Ref } from 'vue'
import type mapboxgl from 'mapbox-gl'

// Maximum number of layer groups supported
// Z-layers are created in reverse order so z-0 is at the TOP
const MAX_LAYERS = 50

// Global state - shared across all instances
const initialized = ref(false)

export function useZLayers(map: Ref<mapboxgl.Map | null>) {
  /**
   * Initialize z-layers on map load
   * Creates empty reference layers that serve as stable anchor points
   * Z-layers are created in reverse order: z-49 first (bottom), z-0 last (top)
   * This ensures layer at position 0 (top of UI list) renders on top of map
   */
  function init(): void {
    if (!map.value || initialized.value) return

    // Add empty source for z-layers
    if (!map.value.getSource('z-layers-empty')) {
      map.value.addSource('z-layers-empty', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      })
    }

    // Create z-layers in REVERSE order so z-0 ends up on TOP
    // z-49 added first (at bottom), z-0 added last (at top)
    for (let i = MAX_LAYERS - 1; i >= 0; i--) {
      if (!map.value.getLayer(`z-${i}`)) {
        map.value.addLayer({
          id: `z-${i}`,
          type: 'fill',
          source: 'z-layers-empty',
          paint: { 'fill-opacity': 0 }
        })
      }
    }

    initialized.value = true
    console.log('[ZLayers] Initialized', MAX_LAYERS, 'z-layers')
  }

  /**
   * Get the beforeId for a layer at the given position.
   * Position 0 = top of UI list = should render on TOP of map.
   *
   * In Mapbox, layers with a beforeId are inserted BEFORE that reference layer
   * in the rendering order, meaning they appear BELOW it visually.
   *
   * Since z-0 is at top and z-49 at bottom:
   * - Position 0 uses z-0 as beforeId, placing the layer immediately below z-0
   *   in the visual stack (appearing on top of the map content)
   * - Position 1 uses z-1 as beforeId, placing the layer below z-1
   * - etc.
   */
  function getBeforeId(position: number): string {
    // Clamp position to valid range
    const clampedPosition = Math.max(0, Math.min(position, MAX_LAYERS - 1))
    return `z-${clampedPosition}`
  }

  /**
   * Check if a layer ID is a z-layer (internal reference layer)
   */
  function isZLayer(layerId: string): boolean {
    return /^z-\d+$/.test(layerId)
  }

  return {
    init,
    getBeforeId,
    isZLayer,
    initialized,
    MAX_LAYERS
  }
}
